import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Box, IconButton, Typography, Tooltip, Chip } from '@mui/material';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import DataArrayRoundedIcon from '@mui/icons-material/DataArrayRounded';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import {
  nodeAtomFamily,
  selectedNodeIdAtom,
  addChildAtom,
  removeNodeAtom,
  moveChildAtom,
  ROOT_ID,
} from '../store/schemaAtoms';
import { NODE_TYPES } from '../store/constants';

const TreeNode = memo(function TreeNode({ id, depth = 0, indexInParent = 0 }) {
  const intl = useIntl();
  const t = (key) => intl.formatMessage({ id: key });
  const node = useAtomValue(nodeAtomFamily(id));
  const [selectedId, setSelectedId] = useAtom(selectedNodeIdAtom);
  const addChild = useSetAtom(addChildAtom);
  const removeNode = useSetAtom(removeNodeAtom);
  const moveChild = useSetAtom(moveChildAtom);

  const isRoot = id === ROOT_ID;
  const isSelected = selectedId === id;

  const select = useCallback(() => setSelectedId(id), [id, setSelectedId]);

  const handleAdd = (kind) => (e) => {
    e.stopPropagation();
    addChild({ parentId: id, kind });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    removeNode(id);
  };

  const handleMove = (direction) => (e) => {
    e.stopPropagation();
    if (!node.parentId) return;
    moveChild({
      parentId: node.parentId,
      fromIndex: indexInParent,
      toIndex: indexInParent + direction,
    });
  };

  const Icon = node.nodeType === NODE_TYPES.OBJECT ? DataObjectRoundedIcon
    : node.nodeType === NODE_TYPES.ARRAY ? DataArrayRoundedIcon
      : TextFieldsRoundedIcon;

  const canHaveChildren = node.nodeType === NODE_TYPES.OBJECT
    || node.nodeType === NODE_TYPES.ARRAY;

  return (
    <Box>
      <Box
        onClick={select}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 8px 6px 8px',
          marginLeft: `${depth * 14}px`,
          borderRadius: '8px',
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: isSelected ? 'rgba(49, 76, 182, 0.08)' : 'transparent',
          borderLeft: isSelected ? '3px solid #314cb6' : '3px solid transparent',
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: isSelected ? 'rgba(49, 76, 182, 0.12)' : 'rgba(0,0,0,0.04)',
            '& .tn-actions': { opacity: 1 },
          },
        }}
      >
        <Icon
          fontSize="small"
          sx={{
            color: node.nodeType === NODE_TYPES.OBJECT ? '#b43e8f'
              : node.nodeType === NODE_TYPES.ARRAY ? '#314cb6' : '#5A5A5A',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, "Roboto Mono", monospace',
            fontWeight: isSelected ? 600 : 500,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isRoot ? '#5A5A5A' : 'inherit',
          }}
        >
          {isRoot
            ? 'root'
            : node.name || (
                <em style={{ color: '#c0392b' }}>{t('SCHEMA_UNNAMED')}</em>
              )}
        </Typography>
        {node.nodeType === NODE_TYPES.PRIMITIVE && (
          <Chip
            label={node.primitiveType}
            size="small"
            sx={{
              height: 18,
              fontSize: '10px',
              fontFamily: 'ui-monospace, monospace',
              backgroundColor: 'rgba(0,0,0,0.06)',
            }}
          />
        )}

        <Box
          className="tn-actions"
          sx={{
            display: 'flex',
            gap: '2px',
            opacity: isSelected ? 1 : 0,
            transition: 'opacity 120ms ease',
          }}
        >
          {canHaveChildren && (
            <>
              <Tooltip title={t('SCHEMA_ADD_FIELD')}>
                <IconButton size="small" onClick={handleAdd(NODE_TYPES.PRIMITIVE)}>
                  <AddRoundedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('SCHEMA_ADD_OBJECT')}>
                <IconButton size="small" onClick={handleAdd(NODE_TYPES.OBJECT)}>
                  <DataObjectRoundedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('SCHEMA_ADD_ARRAY')}>
                <IconButton size="small" onClick={handleAdd(NODE_TYPES.ARRAY)}>
                  <DataArrayRoundedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!isRoot && (
            <>
              <Tooltip title={t('SCHEMA_MOVE_UP')}>
                <IconButton size="small" onClick={handleMove(-1)}>
                  <ArrowUpwardRoundedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('SCHEMA_MOVE_DOWN')}>
                <IconButton size="small" onClick={handleMove(1)}>
                  <ArrowDownwardRoundedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('DELETE')}>
                <IconButton size="small" onClick={handleDelete}>
                  <DeleteOutlineRoundedIcon fontSize="inherit" sx={{ color: '#c0392b' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {node.childrenIds.map((cid, idx) => (
        <TreeNode key={cid} id={cid} depth={depth + 1} indexInParent={idx} />
      ))}
    </Box>
  );
});

TreeNode.propTypes = {
  id: PropTypes.string.isRequired,
  depth: PropTypes.number,
  indexInParent: PropTypes.number,
};

export default TreeNode;
